import { gql, useQuery } from '@apollo/client';
import { Box, Circle, Divider, HStack, Link, Stack, Text } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';

import { NavigationButton } from '@/components/NavigationButton';
import { ECommunityAccessConfig } from '@/graphql-types/globalTypes';
import { useContactSupportLink } from '@/hooks/useContactSupportLink';
import {
  HeadphonesIcon,
  HomeIcon,
  TagIcon,
  UsersIcon,
} from '@/imports/ui/chakra/feather';
import { VideoMeetIcon } from '@/imports/ui/chakra/feather/video-meet';
import { GroupIcon } from '@/imports/ui/components/GroupIcon';
import { useMeetingsLiveIndicators } from '@/modules/meetings/common/hooks/useMeetingsLiveIndicators';

import { VanillaNavigation_Query } from './graphql-types/VanillaNavigation_Query';
import DownloadApp from '@/components/DownloadApp';
import { CollapsibleNavGroupMenu } from '@/components/CollapsibleNavGroupMenu';

const QUERY = gql`
  query VanillaNavigation_Query {
    currentUser {
      _id
      canAccessCommunity
      canAccessCommunityReadOnly
      visibleGroupLinks {
        _id
        userGroupRole
        group {
          _id
          name
          profileImage {
            _id
            imageProxy(input: { h: 36, w: 36, fit: "fill", fill: "solid" })
          }
        }
      }
      ...useContactSupportLink_UserFragment
    }
    tenant {
      result {
        _id
        communityAccessConfig
        ...useContactSupportLink_TenantFragment
      }
    }
  }
  ${useContactSupportLink.userFragment}
  ${useContactSupportLink.tenantFragment}
`;

export const VanillaNavigation: React.FC = () => {
  const query = useQuery<VanillaNavigation_Query>(QUERY);
  const { showMeetingsDot, showLiveLabel } = useMeetingsLiveIndicators({
    audienceOnly: true,
    skip: !query.data?.currentUser?._id,
  });
  const router = useRouter();

  const currentUser = query.data?.currentUser;
  const tenant = query.data?.tenant.result;
  const canAccessCommunity = currentUser
    ? currentUser.canAccessCommunity
    : tenant?.communityAccessConfig === ECommunityAccessConfig.PUBLIC;
  const canAccessCommunityReadOnly = currentUser?.canAccessCommunityReadOnly;
  const meetingsMatchHref = [
    '/community/meetings',
    '/community/quick-call',
    '/community/quick-meet',
  ];

  const {
    contactSupportLink,
    contactSupportText,
    handleContactSupportClick,
  } = useContactSupportLink(currentUser, tenant);

  return (
    <Stack>
      <Stack>
        {canAccessCommunity && (
          <NavigationButton href="/community" leftIcon={<HomeIcon />} isExact>
            Feed
          </NavigationButton>
        )}

        {canAccessCommunityReadOnly && (
          <NavigationButton
            href="/my-company/community"
            leftIcon={<HeadphonesIcon />}
          >
            Community Feed
          </NavigationButton>
        )}

        {canAccessCommunity && (
          <NavigationButton
            href="#"
            leftIcon={<TagIcon />}
            isActive={(() => {
              const onTags = router.pathname.startsWith('/community/tags');
              const isChild =
                onTags &&
                (router.query.tab === 'followed' ||
                  router.query.tab === 'discover');
              return onTags && !isChild;
            })()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            cursor="default"
            pointerEvents="none"
          >
            Tags
          </NavigationButton>
        )}

        {canAccessCommunity && (
          <Stack pl="0" spacing="1">
            <NavigationButton
              href={
                {
                  pathname: '/community/tags',
                  query: { tab: 'discover' },
                } as any
              }
              pl="12"
              isActive={
                router.pathname.startsWith('/community/tags') &&
                router.query.tab === 'discover'
              }
            >
              Discover Tags
            </NavigationButton>
            <NavigationButton
              href={
                {
                  pathname: '/community/tags',
                  query: { tab: 'followed' },
                } as any
              }
              pl="12"
              isActive={
                router.pathname.startsWith('/community/tags') &&
                (typeof router.query.tab !== 'string' ||
                  router.query.tab === 'followed')
              }
            >
              Followed Tags
            </NavigationButton>
          </Stack>
        )}
      </Stack>

      {canAccessCommunity && (
        <>
          <NavigationButton
            href="/community/meetings/list"
            matchHref={meetingsMatchHref}
            leftIcon={
              <Box position="relative">
                <VideoMeetIcon />
                {showMeetingsDot && !showLiveLabel ? (
                  <Circle
                    position="absolute"
                    top="-1"
                    right="-1"
                    size="2"
                    bg="green.400"
                  />
                ) : null}
              </Box>
            }
          >
            <HStack as="span" spacing="2" align="center">
              <Text as="span">Meetings</Text>
              {showLiveLabel ? (
                <Text
                  as="span"
                  fontSize="xs"
                  fontWeight="bold"
                  color="green.500"
                  letterSpacing="wide"
                  textTransform="uppercase"
                >
                  LIVE
                </Text>
              ) : null}
            </HStack>
          </NavigationButton>

          <NavigationButton
            href="#"
            leftIcon={<UsersIcon />}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            cursor="default"
            pointerEvents="none"
          >
            Groups
          </NavigationButton>

          <NavigationButton
            href="/community/groups"
            // leftIcon={<BookOpenIcon />}
            isActive={router.pathname === '/community/groups'}
            pl="12"
          >
            Explore Groups
          </NavigationButton>

          <CollapsibleNavGroupMenu
            defaultOpen={true}
            header={
              <NavigationButton
                href="/community/groups/my-groups"
                isActive={router.pathname === '/community/groups/my-groups'}
                pl="12"
                w="full"
              >
                My Groups
              </NavigationButton>
            }
          >
            <Stack pl="2" spacing="1">
              {currentUser?.visibleGroupLinks?.map(({ group }) => (
                <NavigationButton
                  href={`/community/groups/${group._id}/activity`}
                  matchHref={`/community/groups/${group._id}`}
                  key={group._id}
                  pl="12"
                  leftIcon={
                    <GroupIcon
                      name={group.name}
                      url={group.profileImage?.imageProxy || null}
                    />
                  }
                >
                  {group.name}
                </NavigationButton>
              ))}
            </Stack>
          </CollapsibleNavGroupMenu>
        </>
      )}

      {currentUser && (
        <>
          <Divider />

          <Link
            href={contactSupportLink}
            isExternal
            px="4"
            onClick={handleContactSupportClick}
          >
            {contactSupportText}
          </Link>
        </>
      )}
  
      {canAccessCommunity && (
        <>
          <Divider mb="2" />
          <DownloadApp />
        </>
      )}
    
    </Stack>
  );
};
